import React, { useState, useEffect } from "react";
import { compose } from "recompose";
import { Table, Card, Row, Col, Button, Modal, ModalBody, ModalFooter } from "reactstrap";
import {
  TableContainer,
  Pagination,
  InviteButton,
  withTable,
  EditDeleteButtons,
  ViewingRecords,
} from "components/common/Tables/index";
import Page from "../../components/common/Page";
import { get } from 'lodash';
import PendingButton from "components/common/PendingButton";
import { Trans } from "react-i18next";
import { Link } from "react-router-dom";
import BackButton from "components/refactoring/BackButton";
import { NotificationManager } from "react-notifications";
import { formatDateTime } from "services/util";
import { useOrganisationContext } from "hooks";

const StatCard = ({ label, value, onClick }) => {
  return (
    <Card body className="px-3 py-2 mb-2">
      <div className="d-flex flex-column align-items-center">
        <div
          style={{ lineHeight: "1.5rem", fontSize: ".875rem" }}
          className="font-weight-semibold text-nowrap"
        >
          <Trans>{label}</Trans>
          </div>
        <div>
          <button
            className="btn btn-link"
            onClick={(e) => {
              e.preventDefault();
              onClick(value);
            }}
          >
            {value || 0}
          </button>
        </div>
      </div>
    </Card>
  );
};

export const UserList = ({
  selected,
  match,
  setSelected,
  ordering,
  setOrdering,
  t,
  data,
  limit,
  offset,
  setOffset,
  onDeleted,
  readOnly,
  client,
  pending
}) => {
  const { selectedOrganisation } = useOrganisationContext()
  
  const [stats, setStats] = useState();

  const [selectedGroup, setSelectedGroup] = useState('');
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const refreshData = async () => {
    try {
      const stats = await client.get(`authorization/${selectedOrganisation.id}/company_user`).get("data");
      stats.sort((a, b) => a.id - b.id);
      setStats(stats);
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  };
  
  useEffect(() => {
    (async () => {
      refreshData();
    })();
  }, []);

  const toggle = () => setIsOpen(!isOpen);

  const handleButtonClick = (group) => {
    setSelectedGroup(group);
  };

  const filteredStats = stats?.filter(user => {
    return user.groups.some(group => group.name.includes(selectedGroup));
  });

  const groupCount = stats?.reduce((countMap, user) => {
    user.groups.forEach(group => {
      const groupName = group.name;
      countMap[groupName] = (countMap[groupName] || 0) + 1;
    });
    return countMap;
  }, {});

  const onDelete = (id) => {
    setSelectedUserId(id);
    toggle(); 
  };

  const onConfirm = async () => {
    try {
      await client.delete(`/authorization/${selectedOrganisation.id}/company_user/${selectedUserId}`);
      NotificationManager.success('User deleted successfully!');
      onDeleted(); 
      refreshData();
    } catch (error) {
      console.error('Error deleting user:', error);
      NotificationManager.error('An error occurred while deleting the user.');
    } finally {
      toggle(); 
    }
  };

  const handleInputChange = (event) => {
    setSearch(event.target.value);
  };

  const filteredResults = filteredStats?.filter((item) => {
    const fullName = search ? `${item.first_name} ${item.last_name}`.toLowerCase().includes(search.toLowerCase()) : true;
  
    return fullName;
  });

  const [loading, setLoading] = useState(false);

  const [loadingStates, setLoadingStates] = useState({});

  const handleInviteClick = async (item) => {
    const email = item.email;
    const lang = "en";

    const postData = { email, lang, connected_organisation: selectedOrganisation?.id };

    try {
      setLoadingStates((prevStates) => ({
        ...prevStates,
        [item.id]: true,
      }));

      await client.post('authorization/resend_email', postData);

      console.log("Invite resend successful");
      NotificationManager.success("Invite resend successful");
    } catch (ex) {
      const responseData = get(ex, 'response.data') || {};

      Object.keys(responseData).forEach((key) => {
        const errorMessage = responseData[key];
        if (errorMessage) {
          NotificationManager.error(errorMessage);
        }
      });
    } finally {
      setLoadingStates((prevStates) => ({
        ...prevStates,
        [item.id]: false,
      }));
      console.log("Process complete");
    }
  };
  
  return (
    <Page title="User Management">
    <BackButton/>
      <input
        className="form-control mb-2"
        placeholder={t("Search by first name or last name")}
        type="text"
        id="searchInput"
        value={search}
        onChange={handleInputChange}
      />
      <div className="my-2">
      {stats ? (
        <>
          <Row className="mb-2">
            {Object.keys(groupCount).map((group, index) => (
              <Col lg key={index}>
                <StatCard
                  label={t(group + "s")}
                  value={groupCount[group]}
                  onClick={() => handleButtonClick(group)}
                />
              </Col>
            ))}
            <Col lg>
              <StatCard label="Total" value={stats.length} onClick={() => handleButtonClick('')} />
            </Col>
          </Row>
        </>
      ) : null}
      <InviteButton
        resource="User"
        match={match}
        className="my-3 float-left mb-2"
        readOnly={readOnly}
      />
      </div>
      <TableContainer
        items={filteredResults}
        ordering={ordering}
        onOrderingChanged={setOrdering}
        selected={selected}
        onSelectedChanged={setSelected}
      >
        <Table size="sm" className="small" style={{border: '1px solid #e9ecef'}}>
          <thead>
            <tr>
              <th className="text-left"><Trans>ID </Trans></th>
              <th className="text-left"><Trans>Name</Trans></th>
              <th className="text-left"><Trans>Role</Trans></th>
              <th className="text-left"><Trans>Group</Trans></th>
              <th className="text-left"><Trans>Email</Trans></th>
              <th className="text-left"><Trans>Last login</Trans></th>
              <th className="text-center"><Trans>Active</Trans></th>
              <th className="text-right" />
            </tr>
          </thead>
          <tbody>
            {filteredResults?.map((i) => (
              <tr key={i.id}>
                <td className="text-left">{i.id}</td>
                <td className="text-left">
                <Link to={`${match.url}/${i.id}`} style={{ color: '#72B566', textDecoration: 'none' }}>{i.first_name} {i.last_name}</Link>
                  </td>
                <td className="text-left"><Trans>{i.role}</Trans></td>
                <td className="text-left"><Trans>{i.groups[0]?.name || "Not Assigned"}</Trans></td>
                <td className="text-left">{i.email}</td>
                <td className="text-left">{formatDateTime(i.last_login)}</td>
                <td className="text-center">
                  {i.is_active || !i.get_activation_link ? (
                    <span className="fa fa-check-circle text-success" />
                  ) : (
                    <button
                      className={`btn btn-outline-secondary btn-sm ${loadingStates[i.id] ? 'disabled' : ''}`}
                      onClick={() => !loadingStates[i.id] && handleInviteClick(i)}>
                      {loadingStates[i.id] ? (<Trans>Loading...</Trans>) : <Trans>Re-send</Trans>}
                    </button>
                  )}
                </td>
                <td  className="text-right">
                  <EditDeleteButtons
                    url={match.url}
                    id={i.id}
                    onDelete={() => onDelete(i.id)}
                    readOnly={readOnly}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>

      <Modal isOpen={isOpen} toggle={toggle}>
        <ModalBody>
          <Trans>
            Are you sure you want to delete this user?
          </Trans>
        </ModalBody>
        <ModalFooter>
          <PendingButton color="primary" pending={pending} onClick={onConfirm}>
            Confirm
          </PendingButton>{" "}
          <Button color="secondary" onClick={toggle}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      <ViewingRecords
        offset={offset}
        limit={limit}
        count={data.count}
        className="float-left"
      />
      <div className="float-right">
        <Pagination
          size="sm"
          count={data.count}
          limit={limit}
          offset={offset}
          onOffsetChanged={setOffset}
        />
      </div>
    </Page>
  );
};

const enhance = compose(withTable({ limit: 100 }));

export default enhance(UserList);
